import { FC, useCallback, useMemo } from "react";
import { useDidMount } from "rooks";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../store/hooks";
import { automationProviderSelector } from "../../../../../../../store/automations/selectros/common/fields/automationProviderSelector";
import { automationAccountIdsSelector } from "../../../../../../../store/automations/selectros/common/fields/automationAccountIdsSelector";
import { AccountSelect } from "../../../../../../common/account-select/AccountSelect";
import { allAccountsSelectOptionsSelector } from "../../../../../../../store/accounts/selectors/all-providers/allAccountsSelectOptionsSelector";
import {
  setAutomationData,
  setAutomationErrors,
} from "../../../../../../../store/automations/automationsSlice";
import { automationTypeSelector } from "../../../../../../../store/automations/selectros/common/automationTypeSelector";
import { workflowActionSelector } from "../../../../../../../store/automations/selectros/workflow/workflowActionSelector";
import { automationAccountIdsErrorSelector } from "../../../../../../../store/automations/selectros/common/errors/automationAccountIdsErrorSelector";
import { getOrganisationCurrentFilterTemplatesThunk } from "../../../../../../../store/live-usage-mgmt/thunks/getOrganisationCurrentFilterTemplatesThunk";
import { getActiveAccountStatuses } from "../../../../../../../utils/constants/accounts";
import { AccountOptionType } from "../../../../../../common/account-select/utils/types/types";

export const AutomationAccountsSelection: FC = () => {
  const dispatch = useAppDispatch();

  const provider = useAppSelector(automationProviderSelector);
  const action = useAppSelector(workflowActionSelector);
  const accountIds = useAppSelector(automationAccountIdsSelector);
  const accountIdsError = useAppSelector(automationAccountIdsErrorSelector);
  const automationType = useAppSelector(automationTypeSelector);
  const groupedAllAccounts = useAppSelector(allAccountsSelectOptionsSelector);

  const providerAccounts = useMemo(() => {
    return groupedAllAccounts.filter(
      ({ accountType, status }) =>
        accountType === provider &&
        (!status || getActiveAccountStatuses().has(status)),
    );
  }, [groupedAllAccounts, provider]);

  const options: AccountOptionType[] = useMemo(() => {
    return providerAccounts.map((data) => {
      const isWorkflowCleanActionOnReadAccount =
        automationType === "workflow" &&
        action === "clean_and_notify" &&
        data.accessType === "read";
      const isOffHourOnReadOnly =
        automationType === "offHours" && data.accessType === "read";

      return {
        ...data,
        disabled:
          data.disabled ||
          isOffHourOnReadOnly ||
          isWorkflowCleanActionOnReadAccount ||
          data.status !== "connected",
        tooltipTitle: isWorkflowCleanActionOnReadAccount
          ? tooltipTitle
          : undefined,
      };
    });
  }, [automationType, providerAccounts, action]);

  const changeHandler = useCallback(
    (accountIds: string[]) => {
      dispatch(setAutomationData({ accountIds }));

      if (accountIds.length && accountIdsError) {
        dispatch(setAutomationErrors({ accountIds: "" }));
      }
    },
    [dispatch, accountIdsError],
  );

  useDidMount(() => {
    dispatch(getOrganisationCurrentFilterTemplatesThunk());
  });

  return (
    <AccountSelect
      error={accountIdsError}
      listWidth={700}
      disabled={!provider}
      onChange={changeHandler}
      options={options}
      selectedValue={accountIds || []}
      wrapperVariant="popover"
    />
  );
};

const tooltipTitle =
  "Resources in Read-Only accounts cannot be modified. Switch to Read/Write mode to enable actions.";
